import { createSelector } from '@reduxjs/toolkit';
import { chartOptions } from '@/constant/chart/chart';
import { RootState } from '../types';

// ============================ Loading Selector ============================

export const getDashboardLoading = (state: RootState) => {
  return (
    state?.dashboard?.loading ?? {
      vdiTransactions: false,
      smartCoolerProducts: false,
      alerts: false,
      quikPickProducts: false,
    }
  );
};

// ============================ Dashboard Data ============================

const getDashboardData = function (state: RootState) {
  return state?.dashboard?.dashboard;
};

export const getVdiTransactionCount = createSelector(
  getDashboardData,
  (dashboard) => {
    const sumTotalTransactions = (transactions: any[]) => {
      return transactions.reduce((total: number, item: any) => {
        return total + parseInt(item.total);
      }, 0);
    };

    // Sum total transactions for all categories
    const totalAllCategories = Object.values(
      dashboard?.data?.vdiTransactions ?? {},
    ).reduce((total: number, categoryTransactions: any) => {
      return total + sumTotalTransactions(categoryTransactions);
    }, 0);
    return totalAllCategories;
  },
);

/** Parse a date string to a comparable timestamp. Handles ISO date-only and full ISO strings. */
const parseDateMs = (dateStr: string | undefined | null): number => {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

/** Sort raw API items ascending by their `date` field. */
const sortByDate = <T extends { date?: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => parseDateMs(a.date) - parseDateMs(b.date));

export const getModifiedDashboarddata = createSelector(
  getDashboardData,
  (dashboard) => {
    const finalVdiTransactions: Record<string, any> = {};
    const vdiTransactionsType: string[] = [];
    /**
     * getting a key from the vdiTransactions object and iterating
     */
    for (const key in dashboard?.data?.vdiTransactions) {
      /**
       * Initialize series arrays for accepted and processed
       * */
      const series1 = { name: 'Total', data: [] as number[] };
      const series2 = { name: 'Accepted', data: [] as number[] };
      const series3 = { name: 'Processed', data: [] as number[] };
      /**
       * pushing the key in vdiTransactionsType
       */
      vdiTransactionsType.push(key);
      /**
       * Sort items by date ascending so X-axis always shows chronological order.
       * creating a series and data for each key and pushing it to the respective series
       */
      const sortedVdi = sortByDate(dashboard?.data?.vdiTransactions[key] ?? []);
      sortedVdi.forEach((item: any) => {
        series1.data.push(Number(item?.total) || 0);
        series2.data.push(Number(item?.accepted) || 0);
        series3.data.push(Number(item?.processed) || 0);
      });
      /**
       * merging all the series and chart options and assigning back to particular key
       */
      const finalVdiData = {
        series: [series1, series2, series3],
        options: chartOptions(sortedVdi),
      };
      finalVdiTransactions[key] = finalVdiData;
    }
    /**
     * Initialize series for Alerts — sort by date ascending
     */
    const sortedAlerts = sortByDate(dashboard?.data?.alerts ?? []);
    const countSeries = { name: 'Count', data: [] as number[] };

    sortedAlerts.forEach((item) => {
      countSeries.data.push(Number(item?.count) || 0);
    });

    const finalAlertData = {
      series: [countSeries],
      options: chartOptions(sortedAlerts),
    };

    return {
      ...dashboard,

      data: {
        ...dashboard?.data,
        vdiTransactions: finalVdiTransactions,
        alerts: finalAlertData,
        type: vdiTransactionsType,
      },
    };
  },
);
